import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  updateFormField,
  setCurrentStep,
  setFormComplete,
  setAuthorized,
  showNotification,
  hideNotification,
  setParentRelationship,
  fetchApplicantData,
  manageBasicInfo,
  manageStudentInfo,
  manageParentInfo,
  manageHealthInfo,
  manageStudentAcademicInfo,
  registerStudentFullInformationExisting,
  manageHealthIssue,
} from "../../store/applicationFormDetailAllInOneSlice.js";
import { fetchEnums } from "../../store/enumSlice.js";
import { COUNTRIES } from "../../utils/countries.js";
import validationSchemas from "../validationSchemas.js";
import {
  genderOptions,
  parentTypeOptions,
  RelationshipCategory,
  relationshipToStudentOptions,
  religionOptions,
  occupationTypeOptions,
  phoneTypeOptions,
} from "../../constants/enums.js";
import {
  BasicInformation,
  StudentInformation,
  StudentHistoryForm,
  HealthInformationForm,
  HealthIssueForm,
  ExistingApplicantFinalReviewPage,
} from "./applicationFormUtils.js";
const DetailApplicationFormPage = () => {
  const [newHealthIssue, setNewHealthIssue] = useState("");
  const [selectedIssueType, setSelectedIssueType] = useState("");
  const [isGuardian, setIsGuardian] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    formData = {},
    currentStep = 1,
    formComplete = false,
    status = "idle",
    error = null,
    basicInfoId,
    studentId,

    studentBasicInfo,
    parent1BasicInfo,
    parent2BasicInfo,
    studentInfo,
    parent1Info,
    parent2Info,
    studentHealthInfo,
    healthIssue,
    StudentAcadamicHistory,
  } = useSelector((state) => state.applicationFormDetail);
  const { enums = {}, loading: enumsLoading = false } = useSelector(
    (state) => state.enums
  );
  const applicantData = useSelector(
    (state) => state.applicationFormDetail.applicantData
  );
  const [isAcademicRequired, setIsAcademicRequired] = useState(false);
  const [isHealthIssueRequired, setIsHealthIssueRequired] = useState(false);
  const [isHealthCheckMandatory, setIsHealthCheckMandatory] = useState(false);

  useEffect(() => {
    // Check for token in URL
    const queryParams = new URLSearchParams(location.search);
    const tokenFromUrl = queryParams.get("token");

    if (tokenFromUrl) {
      // If token exists in URL, store it
      localStorage.setItem("token", tokenFromUrl);
      // Clean up URL
      navigate("/existingapplicantform", { replace: true });
      return;
    }

    // If no token in URL, check localStorage
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
  }, [dispatch, navigate, location]);

  useEffect(() => {
    // Fetch applicant data when the component mounts
    const fetchData = async () => {
      try {
        await dispatch(fetchEnums());
        const result = await dispatch(fetchApplicantData()).unwrap();

        // Manually update Formik values if data is fetched
        if (result) {
          formik.setFieldValue("firstName", result.firstName);
          formik.setFieldValue("surName", result.surName);
          formik.setFieldValue("lastName", result.lastName);
          formik.setFieldValue("email", result.contactEmail);
          formik.setFieldValue("gender", result.gender);
          formik.setFieldValue("phoneNumber", result.phoneNumber);
          formik.setFieldValue("nationality", result.nationality);
          formik.setFieldValue("dob", result.dob);
          formik.setFieldValue("birthCountry", result.nationality);
        }
      } catch (error) {
        console.error("Failed to fetch applicant data:", error);
      }
    };

    fetchData();
  }, []);
  const validationSchema = useMemo(() => {
    if (currentStep === 1) {
      return validationSchemas[0](); // Use validationSchemas[0] for steps 1, 4, 6, 8
    } else if (currentStep === 2) {
      return validationSchemas[1](); // Use validationSchemas[2] for step 2
    } else if (currentStep === 3) {
      return validationSchemas[2](); // Use validationSchemas[2] for step 3
    } else if (currentStep === 4) {
      return validationSchemas[4](isHealthCheckMandatory); // Use validationSchemas[3] for step 10
    } else if (currentStep === 5) {
      return validationSchemas[5](isHealthIssueRequired); // Use validationSchemas[3] for step 10
    } else if (currentStep === 6) {
      return validationSchemas[6](isAcademicRequired); // Use validationSchemas[4] for step 11
    }
  }, [
    currentStep,
    isAcademicRequired,
    isHealthIssueRequired,
    isHealthCheckMandatory,
  ]);

  const formik = useFormik({
    initialValues: {
      // Student Basic Information
      firstName: "",
      surName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      dob: "",
      gender: "",
      nationality: "",
      birthCountry: "",
      passportCountry: "",
      passportNumber: "",
      passportCopyFile: null,
      photo: "",

      // Student Information
      primaryLanguage: "",
      otherLanguages: "",
      religion: "",
      birthCertificateFile: "",

      // Parent relationship Information
      parentRelationship: "",

      // Parent Information
      parentType: "",
      parentGender: "",
      parentWorkPlace: "",
      parentWorkPosition: "",
      parentAddress: "",
      parentPoBox: "",
      parentSubCity: "",
      parentKebele: "",
      parentHouseNumber: "",
      parentMobilePhone: "",
      parentHomePhone: "",
      parentOfficePhone: "",
      parentOtherPhone: "",
      parentFaxNumber: "",
      parentPreferredPhoneType: "",
      parentEmail: "",
      parentRelationshipToStudent: "",
      parentOccupationType: "",

      // Health Information
      referenceHospital: "",
      doctorName: "",
      hospitalNumber: "",
      doctorNumber: "",
      healthDescription: "",
      vaccinationStatus: "",
      vaccinationExplanation: "",
      healthIssues: [],

      //Student Academic History
      schoolName: "",
      schoolPhoneNumber: "",
      gradeFrom: "",
      gradeTo: "",
      entryDate: "2017-03-07",
      exitDate: "2017-03-07",
      schoolAddress: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        let result;
        switch (currentStep) {
          case 1: //StudentBasicInfo
            // Basic Information
            result = await dispatch(manageBasicInfo(values)).unwrap();
            //  dispatch(setBasicInfo(values)).unwrap();
            if (result) {
              dispatch(
                showNotification({
                  message: "Student Basic information saved successfully!",
                  type: "success",
                })
              );

              dispatch(setCurrentStep(currentStep + 1));
            } else {
              throw new Error("Failed to save basic information");
            }
            break;
          case 2: // Student Information
            result = await dispatch(manageStudentInfo(values)).unwrap();
            if (result) {
              dispatch(
                showNotification({
                  message: "Student information saved successfully!",
                  type: "success",
                })
              );

              dispatch(setCurrentStep(currentStep + 1));
            } else {
              throw new Error("Failed to save student information");
            }
            break;
          case 3: // Parent type Information
            //dispatch(setParentType(values));
            dispatch(
              showNotification({
                message: "Parent type information saved successfully!",
                type: "success",
              })
            );
            if (values.parentRelationship === "0") {
              //Biological parent
              formik.setFieldValue("parentType", 0);
              console.log("parentType", values.parentType);
              dispatch(setCurrentStep(4));
            } else if (values.parentRelationship === "1") {
              //Legal guardian
              formik.setFieldValue("parentType", 2);
              console.log("parentType", values.parentType);
              dispatch(setCurrentStep(4));
            } else if (values.parentRelationship === "2") {
              //Other
              dispatch(setCurrentStep(20)); //invalid step
              throw new Error("It is not allowed to process the form");
            }
            break;
          case 4: // Student health Information
            console.log("healthInformation: values", values);

            result = await dispatch(
              manageHealthInfo({ data: values, isHealthCheckMandatory })
            ).unwrap();
            if (result) {
              dispatch(
                showNotification({
                  message: "Guardian's information saved successfully!",
                  type: "success",
                })
              );

              dispatch(setCurrentStep(currentStep + 1));
            } else {
              throw new Error("Failed to save Guardian's information");
            }
            break;
          case 5: // Student health Issue
            console.log("values", values);
            result = await dispatch(
              manageHealthIssue({ data: values, isHealthIssueRequired })
            ).unwrap();
            if (result) {
              dispatch(
                showNotification({
                  message: "Guardian's information saved successfully!",
                  type: "success",
                })
              );

              dispatch(setCurrentStep(currentStep + 1));
            } else {
              throw new Error("Failed to save Guardian's information");
            }
            break;
          case 6: // Student Academic History Information
            console.log("values: ", values);
            result = await dispatch(
              manageStudentAcademicInfo({ isAcademicRequired, data: values })
            ).unwrap();
            if (result) {
              dispatch(
                showNotification({
                  message: "Student's academic information saved successfully!",
                  type: "success",
                })
              );
              dispatch(setCurrentStep(currentStep + 1));
            } else {
              throw new Error("Failed to save Student's academic information");
            }
            console.log("state.StudentAcadamicHistory", StudentAcadamicHistory);

            break;
          case 7: // manage summary
            dispatch(setCurrentStep(currentStep + 1));
            dispatch(
              registerStudentFullInformationExisting({
                studentBasicInfo,
                studentInfo,
                studentHealthInfo,
                healthIssue,
                StudentAcadamicHistory,
              })
            );
            break;
          case 8:
            dispatch(setCurrentStep(currentStep + 1));
            break;

          default:
            throw new Error("Invalid step");
        }
      } catch (error) {
        // Handle submission error
        console.error("Submission error:", error);

        // Show error notification
        dispatch(
          showNotification({
            message: error.message || "Failed to submit information",
            type: "error",
          })
        );
      }
    },
  });

  const handleNextStep = async () => {
    try {
      // Trigger Formik's form submission
      await formik.submitForm();
    } catch (error) {
      console.error("Next step error:", error);
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (currentStep !== 1) {
      formik.handleChange(e);
    } else if (
      name === "dob" ||
      name === "birthCountry" ||
      name === "passportCountry" ||
      name === "passportNumber" ||
      name === "passportCopyFile" ||
      name === "nationality" ||
      name === "gender" ||
      name === "phoneNumber" ||
      name === "photo"
    )
      formik.handleChange(e);
    dispatch(updateFormField({ field: name, value })); // Update Redux state
  };

  useEffect(() => {
    if (currentStep !== 1) {
      if (currentStep === 4 || currentStep === 6 || currentStep === 8) {
        formik.setFieldValue("firstName", ""); // Reset value for "gender" field
        formik.setFieldValue("surName", ""); // Reset value for "gender" field
        formik.setFieldValue("lastName", ""); // Reset value for "gender" field
      }
      if (currentStep === 4) {
        formik.setFieldValue(
          "gender",
          0 //Male
        );
      }
      // Reset value for "gender" field
      else if (currentStep === 5) {
        formik.setFieldValue("parentGender", 0);
        formik.setFieldValue("parentRelationshipToStudent", "Father");
      }
      // Reset value for "gender" field to Female
      else if (currentStep === 6) formik.setFieldValue("gender", 1);
      // Reset value for "gender" field to Female
      else if (currentStep === 7) {
        formik.setFieldValue("parentGender", 1);
        formik.setFieldValue("parentRelationshipToStudent", "Mother");
      } // Reset value for "gender" field to Female
      else if (currentStep === 9) {
        formik.setFieldValue("parentRelationshipToStudent", "Guardian");
      } // Reset value for "gender" field to Female

      formik.setFieldValue("nationality", ""); // Reset value for "gender" field
      // Add other fields if needed, like "firstName", "surName", etc.
    }
  }, [currentStep]);

  const inputStyle =
    "block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm";
  const errorStyle =
    "border-red-500 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500";
  const selectStyle =
    "block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm";

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <BasicInformation
              formik={formik}
              handleFieldChange={handleFieldChange}
              parentTypeOptions={parentTypeOptions}
              genderOptions={genderOptions}
              phoneTypeOptions={phoneTypeOptions}
              relationshipToStudentOptions={relationshipToStudentOptions}
              occupationTypeOptions={occupationTypeOptions}
              selectStyle={selectStyle}
              inputStyle={inputStyle}
              errorStyle={errorStyle}
              currentStep={currentStep}
              title={"Basic Student Data"}
            />
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <StudentInformation
              formik={formik}
              handleFieldChange={handleFieldChange}
              selectStyle={selectStyle}
              inputStyle={inputStyle}
              errorStyle={errorStyle}
              religionOptions={religionOptions}
            />
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">
              Parent Type Information - Step {currentStep}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="parentType"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Parent Relationship Category{" "}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  id="parentRelationship"
                  name="parentRelationship"
                  value={formik.values.parentRelationship}
                  onChange={handleFieldChange}
                  onBlur={formik.handleBlur}
                  className={`${selectStyle} ${
                    formik.touched.parentCategory &&
                    formik.errors.parentCategory
                      ? errorStyle
                      : ""
                  }`}
                >
                  <option value="">Select parent relationship</option>
                  {RelationshipCategory.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {formik.touched.parentType && formik.errors.parentType && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.parentType}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <HealthInformationForm
              formik={formik}
              handleFieldChange={handleFieldChange}
              newHealthIssue={newHealthIssue}
              setNewHealthIssue={setNewHealthIssue}
              selectedIssueType={selectedIssueType}
              setSelectedIssueType={setSelectedIssueType}
              inputStyle={inputStyle}
              errorStyle={errorStyle}
              selectStyle={selectStyle}
              isHealthCheckMandatory={isHealthCheckMandatory}
              setIsHealthCheckMandatory={setIsHealthCheckMandatory}
            />
          </div>
        );
      case 5:
        return (
          <div className="space-y-6">
            <HealthIssueForm
              formik={formik}
              handleFieldChange={handleFieldChange}
              newHealthIssue={newHealthIssue}
              setNewHealthIssue={setNewHealthIssue}
              selectedIssueType={selectedIssueType}
              setSelectedIssueType={setSelectedIssueType}
              inputStyle={inputStyle}
              errorStyle={errorStyle}
              selectStyle={selectStyle}
              isHealthIssueRequired={isHealthIssueRequired}
              setIsHealthIssueRequired={setIsHealthIssueRequired}
            />
          </div>
        );
      case 6:
        // parents additional information
        return (
          <div className="space-y-6">
            <StudentHistoryForm
              formik={formik}
              handleFieldChange={formik.handleChange}
              inputStyle={inputStyle}
              selectStyle={selectStyle}
              errorStyle={errorStyle}
              isAcademicRequired={isAcademicRequired}
              setIsAcademicRequired={setIsAcademicRequired}
            />
          </div>
        );
      case 7:
        return (
          <div className="space-y-6">
            <ExistingApplicantFinalReviewPage
              studentBasicInfo={studentBasicInfo}
              studentInfo={studentInfo}
              studentHealthInfo={studentHealthInfo}
              healthIssue={healthIssue}
              StudentAcadamicHistory={StudentAcadamicHistory}
            />
          </div>
        );
      case 8:
        return error ? (
          <div className="space-y-6">
            <h4 className="text-xl font-semibold text-red-600">
              Oops! Something went wrong.
            </h4>
            <p className="text-sm text-gray-700">Error details: {error}</p>
          </div>
        ) : (
          <div className="space-y-6">
            <h4 className="text-xl font-semibold text-gray-900">
              Congradulation!!!!
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="parentType"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Sucessfully completed!!!!
                  <span className="text-red-500">*</span>
                </label>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (status === "loading") {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  }
  const lastStep = 8;
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {currentStep < lastStep && (
          <h2 className="text-2xl font-bold mb-6">
            Admission Form - Step {currentStep}
          </h2>
        )}
        <form onSubmit={formik.handleSubmit} className="space-y-6">
          {renderStepContent()}
          <div className="flex justify-end space-x-4">
            {currentStep === 1 && (
              <button
                type="button"
                onClick={handleNextStep}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Next
              </button>
            )}
            {currentStep > 1 && currentStep < lastStep && (
              <button
                type="button"
                onClick={() => dispatch(setCurrentStep(currentStep - 1))}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Previous
              </button>
            )}

            {currentStep > 1 && currentStep < lastStep - 1 && (
              <button
                type="submit"
                disabled={formik.isSubmitting || status === "loading"}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Next
              </button>
            )}

            {currentStep === lastStep - 1 && (
              <button
                type="submit"
                disabled={formik.isSubmitting || status === "loading"}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Submit
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default DetailApplicationFormPage;
